import React, { useState, useMemo, useEffect, useRef } from "react";
import * as d3 from "d3";
import { useTheme } from "styled-components";
import api from "../../services/api";
import {
  FiTruck,
  FiSearch,
  FiDollarSign,
  FiPercent,
  FiPackage,
  FiMapPin,
  FiCalendar,
  FiBarChart2,
  FiUsers,
  FiTrendingUp,
  FiLoader,
  FiX,
} from "react-icons/fi";
import { BiSpreadsheet } from "react-icons/bi";
import { HiOutlineChartBar } from "react-icons/hi";
import { GoGlobe, GoLocation, GoLaw, GoSearch, GoGraph, GoArrowUp, GoArrowDown, GoPackage } from "react-icons/go";
import { Container, MainContent } from "./styles";
import EmptyState from "../../components/EmptyState";
import EntityListRow, { getTagColor } from "../../components/EntityListRow";
import SupplierDetail from "./supplier";
import SuppliersLoadingSkeleton from "./SuppliersLoadingSkeleton";
import SuppliersStatsLoadingSkeleton from "./SuppliersStatsLoadingSkeleton";
import SuppliersReformaLoadingSkeleton from "./SuppliersReformaLoadingSkeleton";
import usePersistedState from "../../hooks/usePersistedState";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

// Cores para fornecedores baseadas no CNPJ
const getSupplierColor = (cnpj) => {
  const colors = [
    "#2563eb",
    "#059669",
    "#b45309",
    "#dc2626",
    "#7c3aed",
    "#0891b2",
    "#4338ca",
    "#0f766e",
    "#be185d",
    "#1e40af",
  ];
  const hash = cnpj.split("").reduce((a, b) => a + b.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// Formatar números
const formatNumber = (value) => {
  if (!value || isNaN(value)) return "R$ 0";
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(0)}K`;
  }
  return `R$ ${value.toFixed(0)}`;
};

// Calcular total de impostos de um fornecedor
const calculateTotalTaxes = (supplier) => {
  return (
    (supplier.icms || 0) +
    (supplier.pisCofins || 0) +
    (supplier.ibs || 0) +
    (supplier.cbs || 0) +
    (supplier.is || 0)
  );
};

// Componente do Mapa do Brasil usando D3.js
const BrazilMapCard = ({ data, formatNumber }) => {
  const svgRef = useRef();
  const [geoData, setGeoData] = useState(null);
  const [hoveredState, setHoveredState] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Mapeamento de UF para nome do estado
  const nomeEstados = {
    AC: "Acre",
    AL: "Alagoas",
    AP: "Amapá",
    AM: "Amazonas",
    BA: "Bahia",
    CE: "Ceará",
    DF: "Distrito Federal",
    ES: "Espírito Santo",
    GO: "Goiás",
    MA: "Maranhão",
    MT: "Mato Grosso",
    MS: "Mato Grosso do Sul",
    MG: "Minas Gerais",
    PA: "Pará",
    PB: "Paraíba",
    PR: "Paraná",
    PE: "Pernambuco",
    PI: "Piauí",
    RJ: "Rio de Janeiro",
    RN: "Rio Grande do Norte",
    RS: "Rio Grande do Sul",
    RO: "Rondônia",
    RR: "Roraima",
    SC: "Santa Catarina",
    SP: "São Paulo",
    SE: "Sergipe",
    TO: "Tocantins",
  };

  // Processar dados por UF - compatível com API e formato legado
  const ufData = useMemo(() => {
    const ufMap = new Map();
    if (!data || data.length === 0) return ufMap;

    data.forEach((item) => {
      // API envia: { id: "SP", faturamento: X, count: Y }
      // Legacy envia: { uf: "SP", compras: X }
      const uf = item.id || item.uf;
      const valor = item.faturamento || item.compras || 0;
      const count = item.count || 1;
      
      if (uf) {
        if (!ufMap.has(uf)) {
          ufMap.set(uf, { uf, compras: 0, count: 0 });
        }
        const d = ufMap.get(uf);
        d.compras += valor;
        d.count += count;
      }
    });
    return ufMap;
  }, [data]);

  const totalCompras = useMemo(() => {
    return Array.from(ufData.values()).reduce((sum, d) => sum + d.compras, 0);
  }, [ufData]);

  // Carregar GeoJSON do Brasil
  useEffect(() => {
    fetch(
      "https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/brazil-states.geojson"
    )
      .then((res) => res.json())
      .then((data) => setGeoData(data))
      .catch((err) => console.error("Erro ao carregar GeoJSON:", err));
  }, []);

  // Renderizar mapa com D3
  useEffect(() => {
    if (!geoData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    const width = 420;
    const height = 420;

    svg.selectAll("*").remove();

    const projection = d3
      .geoMercator()
      .center([-52, -15])
      .scale(560)
      .translate([width / 2, height / 2]);

    const path = d3.geoPath().projection(projection);

    // Escala de cores
    const getColor = (uf) => {
      const stateData = ufData.get(uf);
      if (!stateData || stateData.compras === 0) return "rgba(55, 65, 81, 0.5)";
      const maxCompras = Math.max(
        ...Array.from(ufData.values()).map((d) => d.compras),
        1
      );
      const intensity = stateData.compras / maxCompras;
      if (intensity > 0.7) return "#1d4ed8";
      if (intensity > 0.5) return "#3b82f6";
      if (intensity > 0.3) return "#60a5fa";
      if (intensity > 0.1) return "#93c5fd";
      return "#bfdbfe";
    };

    // Desenhar estados
    svg
      .selectAll("path")
      .data(geoData.features)
      .enter()
      .append("path")
      .attr("d", path)
      .attr("fill", (d) => {
        const uf =
          d.properties.sigla ||
          d.properties.name?.substring(0, 2)?.toUpperCase();
        return getColor(uf);
      })
      .attr("stroke", "rgba(255, 255, 255, 0.3)")
      .attr("stroke-width", 0.5)
      .attr("cursor", "pointer")
      .style("opacity", 0.8)
      .style("transition", "all 0.2s ease")
      .on("mouseover", function (event, d) {
        const uf =
          d.properties.sigla ||
          d.properties.name?.substring(0, 2)?.toUpperCase();
        const stateData = ufData.get(uf) || { uf, compras: 0, count: 0 };
        d3.select(this)
          .attr("stroke", "#fff")
          .attr("stroke-width", 1.5)
          .style("opacity", 1);
        setHoveredState({ ...stateData, nome: nomeEstados[uf] || uf });
        setMousePos({ x: event.pageX, y: event.pageY });
      })
      .on("mousemove", (event) =>
        setMousePos({ x: event.pageX, y: event.pageY })
      )
      .on("mouseout", function () {
        d3.select(this)
          .attr("stroke", "rgba(255, 255, 255, 0.3)")
          .attr("stroke-width", 0.5)
          .style("opacity", 0.8);
        setHoveredState(null);
      });

    // Labels dos estados
    svg
      .selectAll("text")
      .data(geoData.features)
      .enter()
      .append("text")
      .attr("x", (d) => path.centroid(d)[0])
      .attr("y", (d) => path.centroid(d)[1])
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .attr("font-size", "8px")
      .attr("font-weight", "600")
      .attr("fill", "#fff")
      .attr("pointer-events", "none")
      .style("text-shadow", "1px 1px 2px rgba(0,0,0,0.8)")
      .text(
        (d) =>
          d.properties.sigla ||
          d.properties.name?.substring(0, 2)?.toUpperCase()
      );
  }, [geoData, ufData]);

  return (
    <div className="stats-card mapa-brasil">
      <div className="card-header">
        <div className="header-title">
          <GoGlobe />
          <h4>Compras por Estado</h4>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          flex: 1,
          minHeight: "500px",
        }}
      >
        <svg
          ref={svgRef}
          width="420"
          height="420"
          viewBox="0 0 420 420"
          style={{ maxWidth: "100%" }}
        />
        {/* Legenda */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            justifyContent: "center",
            marginTop: "12px",
          }}
        >
          {[
            { color: "#1d4ed8", label: "Alto" },
            { color: "#60a5fa", label: "Médio" },
            { color: "#bfdbfe", label: "Baixo" },
            { color: "rgba(55, 65, 81, 0.5)", label: "Sem dados" },
          ].map((item) => (
            <div
              key={item.label}
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "2px",
                  background: item.color,
                }}
              ></div>
              <span style={{ fontSize: "0.65em", color: "#94a3b8" }}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Tooltip */}
      {hoveredState && (
        <div
          style={{
            position: "fixed",
            left: mousePos.x + 15,
            top: mousePos.y + 15,
            zIndex: 999999,
            background: "rgba(0, 0, 0, 0.9)",
            color: "white",
            padding: "12px 16px",
            borderRadius: "8px",
            fontSize: "13px",
            pointerEvents: "none",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.6)",
            minWidth: "200px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: "14px",
              marginBottom: "8px",
              borderBottom: "1px solid rgba(255,255,255,0.2)",
              paddingBottom: "6px",
            }}
          >
            {hoveredState.uf} - {hoveredState.nome}
          </div>
          <div
            style={{
              color: "#10b981",
              fontWeight: 700,
              fontSize: "16px",
              marginBottom: "6px",
            }}
          >
            {formatNumber(hoveredState.compras)}
          </div>
          <div
            style={{ color: "#60a5fa", fontSize: "12px", marginBottom: "4px" }}
          >
            {totalCompras > 0
              ? ((hoveredState.compras / totalCompras) * 100).toFixed(1)
              : 0}
            % do total
          </div>
          <div style={{ color: "#94a3b8", fontSize: "11px" }}>
            {hoveredState.count} fornecedor
            {hoveredState.count !== 1 ? "es" : ""}
          </div>
        </div>
      )}
    </div>
  );
};

const Suppliers = () => {
  const theme = useTheme();
  // Tab ativa persistida
  const [activeTab, setActiveTab] = usePersistedState(
    "vidal_suppliers_tab",
    "geral"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    kpis: { clientes: 0, faturamento: 0, impostos: 0, ticketMedio: 0 },
    impostosDetalhados: { icms: 0, pisCofins: 0, ibs: 0, cbs: 0, is: 0 },
    geo: [],
    lista: [],
    evolucao: [],
    tipoPessoa: []
  });
  
  // Infinite scroll states for geral tab (local pagination)
  const [displayedCount, setDisplayedCount] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerTarget = useRef(null);

  // API pagination states for reforma tab
  const [reformaSuppliers, setReformaSuppliers] = useState([]);
  const [reformaPage, setReformaPage] = useState(1);
  const [reformaHasMore, setReformaHasMore] = useState(true);
  const [reformaIsLoadingMore, setReformaIsLoadingMore] = useState(false);
  const reformaObserverTarget = useRef(null);

  // Search state for visual feedback
  const [isSearching, setIsSearching] = useState(false);

  // Buscar dados da API
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const projectId = localStorage.getItem("vidal_selected_project");
        if (!projectId) {
          console.warn("Nenhum projeto selecionado");
          setLoading(false);
          return;
        }
        
        // Usar a nova API de fornecedores
        console.log('[Fornecedores] Buscando dados para projeto:', projectId);
        const response = await api.get('/fornecedores/dashboard', {
          headers: { projeto: projectId }
        });
        
        console.log('[Fornecedores] Response completo:', response);
        console.log('[Fornecedores] Response.data:', response.data);
        
        if (response.data && response.data.success) {
          console.log('[Fornecedores] Dados recebidos da API:', response.data.data);
          console.log('[Fornecedores] KPIs:', response.data.data.kpis);
          console.log('[Fornecedores] Geo:', response.data.data.geo);
          console.log('[Fornecedores] Lista:', response.data.data.lista?.length, 'fornecedores');
          setDashboardData(response.data.data);
        } else {
          console.warn('[Fornecedores] API não retornou success=true');
        }
      } catch (error) {
        console.error("Erro ao carregar dashboard fornecedores:", error);
        // Em caso de erro, manter dados mock
        console.log('[Fornecedores] Usando dados mock devido ao erro');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  // Usar apenas dados reais da API
  const suppliersData = dashboardData.lista || [];

  // Filtrar fornecedores
  const filteredSuppliers = suppliersData.filter(
    (supplier) =>
      supplier.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (supplier.cnpj && supplier.cnpj.includes(searchTerm)) ||
      (supplier.categoria || supplier.segmento || "").toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Suppliers to display with pagination
  const displayedSuppliers = filteredSuppliers.slice(0, displayedCount);
  const hasMore = displayedCount < filteredSuppliers.length;
  
  // Reset displayed count when search term changes
  useEffect(() => {
    setDisplayedCount(20);
    if (searchTerm) {
      setIsSearching(true);
      const timer = setTimeout(() => setIsSearching(false), 300);
      return () => clearTimeout(timer);
    }
  }, [searchTerm]);
  
  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          setIsLoadingMore(true);
          setTimeout(() => {
            setDisplayedCount(prev => Math.min(prev + 20, filteredSuppliers.length));
            setIsLoadingMore(false);
          }, 300);
        }
      },
      { threshold: 0.1 }
    );
    
    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }
    
    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, filteredSuppliers.length]);

  // Buscar lista paginada de fornecedores para reforma
  const fetchReformaSuppliers = async (page = 1, append = false) => {
    try {
      if (!append) setLoading(true);
      setReformaIsLoadingMore(true);
      
      const projectId = localStorage.getItem("vidal_selected_project");
      const response = await api.get(`/suppliers/${projectId}/list`, {
        params: { page, limit: 50 }
      });
      
      if (response.data.success) {
        const newSuppliers = response.data.data.fornecedores || [];
        if (append) {
          setReformaSuppliers(prev => [...prev, ...newSuppliers]);
        } else {
          setReformaSuppliers(newSuppliers);
        }
        setReformaPage(page);
        setReformaHasMore(response.data.data.pagination.hasMore);
      }
    } catch (err) {
      console.error('Erro ao buscar fornecedores paginados:', err);
    } finally {
      setLoading(false);
      setReformaIsLoadingMore(false);
    }
  };

  // Carregar fornecedores quando aba reforma for ativada (apenas uma vez)
  const hasLoadedReforma = useRef(false);
  
  useEffect(() => {
    if (activeTab === 'reforma' && !hasLoadedReforma.current) {
      hasLoadedReforma.current = true;
      fetchReformaSuppliers(1, false);
    }
  }, [activeTab]);

  // Infinite scroll observer for reforma tab API pagination
  useEffect(() => {
    if (activeTab !== 'reforma') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && reformaHasMore && !reformaIsLoadingMore) {
          const nextPage = reformaPage + 1;
          fetchReformaSuppliers(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );
    
    if (reformaObserverTarget.current) {
      observer.observe(reformaObserverTarget.current);
    }
    
    return () => {
      if (reformaObserverTarget.current) {
        observer.unobserve(reformaObserverTarget.current);
      }
    };
  }, [activeTab, reformaHasMore, reformaIsLoadingMore, reformaPage]);

  // Calcular totais (usar API se disponível)
  const totals = dashboardData.kpis?.compras > 0
    ? { compras: dashboardData.kpis.compras, impostos: dashboardData.kpis.impostos }
    : suppliersData.reduce(
        (acc, supplier) => {
          acc.compras += supplier.compras || supplier.faturamento || 0;
          acc.impostos +=
            (supplier.icms || 0) +
            (supplier.pisCofins || 0) +
            (supplier.ibs || 0) +
            (supplier.cbs || 0) +
            (supplier.is || 0);
          return acc;
        },
        { compras: 0, impostos: 0 }
      );

  // Calcular totais detalhados de impostos (usar API se disponível)
  const taxTotals = dashboardData.impostosDetalhados?.icms !== undefined
    ? dashboardData.impostosDetalhados
    : suppliersData.reduce(
        (acc, supplier) => {
          acc.icms += supplier.icms || 0;
          acc.pisCofins += supplier.pisCofins || 0;
          acc.ibs += supplier.ibs || 0;
          acc.cbs += supplier.cbs || 0;
          acc.is += supplier.is || 0;
          return acc;
        },
        { icms: 0, pisCofins: 0, ibs: 0, cbs: 0, is: 0 }
      );

  // Cores fixas por categoria (incluindo tipoPessoa do backend)
  const categoryColors = {
    Distribuidor: "#3b82f6", // Azul
    Indústria: "#10b981", // Verde
    Atacadista: "#f59e0b", // Laranja
    Importador: "#8b5cf6", // Roxo
    Cooperativa: "#06b6d4", // Ciano
    Serviços: "#ec4899", // Rosa
    "Pessoa Jurídica": "#3b82f6", // Azul
    "Pessoa Física": "#10b981", // Verde
    "PJ": "#3b82f6",
    "PF": "#10b981",
    "Geral": "#64748b",
  };

  // Mapeamento de tipoPessoa para nome legível
  const tipoPessoaLabels = {
    "PJ": "Pessoa Jurídica",
    "PF": "Pessoa Física",
    "Indefinido": "Outros"
  };

  // Processar categorias - usar tipoPessoa se segmento for apenas "Geral"
  const categoriesData = suppliersData.reduce((acc, supplier) => {
    // Priorizar categoria/segmento se não for "Geral", senão usar tipoPessoa
    let cat = supplier.categoria || supplier.segmento;
    if (!cat || cat === "Geral") {
      cat = tipoPessoaLabels[supplier.tipoPessoa] || supplier.tipoPessoa || "Outros";
    }
    
    const existing = acc.find((c) => c.name === cat);
    if (existing) {
      existing.count++;
      existing.compras += supplier.compras || supplier.faturamento || 0;
    } else {
      acc.push({
        name: cat,
        count: 1,
        compras: supplier.compras || supplier.faturamento || 0,
        color: categoryColors[cat] || "#64748b",
      });
    }
    return acc;
  }, []);

  const totalCategories = categoriesData.length;

  // Processar dados por UF
  const ufColors = {
    "SP": "#3b82f6", "RJ": "#10b981", "MG": "#f59e0b", "RS": "#ef4444",
    "PR": "#8b5cf6", "SC": "#06b6d4", "BA": "#ec4899", "DF": "#6366f1",
    "GO": "#14b8a6", "PE": "#f97316", "CE": "#84cc16", "PA": "#a855f7",
    "MT": "#eab308", "MS": "#22c55e", "ES": "#64748b", "OUTROS": "#94a3b8",
  };

  const ufData = useMemo(() => {
    const grouped = suppliersData.reduce((acc, supplier) => {
      const uf = supplier.uf || "OUTROS";
      const existing = acc.find((u) => u.name === uf);
      const valor = supplier.compras || supplier.faturamento || 0;
      if (existing) {
        existing.count++;
        existing.compras += valor;
      } else {
        acc.push({
          name: uf,
          count: 1,
          compras: valor,
          color: ufColors[uf] || ufColors["OUTROS"],
        });
      }
      return acc;
    }, []);
    return grouped.sort((a, b) => b.compras - a.compras);
  }, [suppliersData]);

  const maxUfCompras = Math.max(...ufData.map((u) => u.compras), 1);

  // Dados mensais mock
  const monthlyData = {
    month: "Dezembro 2024",
    newSuppliers: 2,
    activeSuppliers: 8,
    compras: 2150000,
    icms: 0,
    pisCofins: 107500,
    ibs: 75250,
    cbs: 0,
    is: 0,
  };

  // Loading state - será renderizado dentro do Container principal

  // Se um fornecedor foi selecionado, exibir o detalhe
  if (selectedSupplier) {
    return (
      <SupplierDetail
        fornecedor={selectedSupplier}
        onVoltar={() => setSelectedSupplier(null)}
        initialTab={activeTab === "reforma" ? "reforma" : "geral"}
      />
    );
  }

  return (
    <Container>
      {/* Header */}
      <div className="header-info">
        <div className="header-details">
          <h3>Dashboard de Fornecedores</h3>
          <div className="header-meta">
            <span className="status-tag active">Sistema Online</span>
            <span className="update-tag">{dashboardData.kpis?.fornecedores || suppliersData.length} fornecedores</span>
          </div>
        </div>
      </div>

      {/* Tabs - Geral / Estatísticas / Reforma Tributária */}
      <div className="tabs-header">
        <button
          className={`tab ${activeTab === "geral" ? "active" : ""}`}
          onClick={() => setActiveTab("geral")}
        >
          <BiSpreadsheet />
          Geral
        </button>
        <button
          className={`tab ${activeTab === "estatisticas" ? "active" : ""}`}
          onClick={() => setActiveTab("estatisticas")}
        >
          <HiOutlineChartBar />
          Estatísticas
        </button>
        <button
          className={`tab ${activeTab === "reforma" ? "active" : ""}`}
          onClick={() => setActiveTab("reforma")}
        >
          <GoLaw />
          Reforma Tributária
        </button>
      </div>

      {/* Empty State — nenhum SPED carregado */}
      {!loading && suppliersData.length === 0 && (
        <EmptyState screen="suppliers" />
      )}

      {/* Loading State */}
      {loading && activeTab === "geral" && (
        <SuppliersLoadingSkeleton isDark={theme.body !== "#fafbfc"} />
      )}
      
      {loading && activeTab === "estatisticas" && (
        <SuppliersStatsLoadingSkeleton isDark={theme.body !== "#fafbfc"} />
      )}
      
      {loading && activeTab === "reforma" && (
        <SuppliersReformaLoadingSkeleton isDark={theme.body !== "#fafbfc"} />
      )}

      {activeTab === "geral" && !loading && suppliersData.length > 0 && (
        <>
          {/* KPI Cards Principais */}
          <div className="kpi-cards main-stats">
            <div className="kpi-card primary">
              <div className="card-content">
                <div className="thumb">
                  <FiUsers />
                </div>
                <div className="text">
                  <p>{dashboardData.kpis?.fornecedores || suppliersData.length}</p>
                  <p>Fornecedores Cadastrados</p>
                </div>
              </div>
            </div>

            <div className="kpi-card success">
              <div className="card-content">
                <div className="thumb">
                  <FiDollarSign />
                </div>
                <div className="text">
                  <p>{formatNumber(totals.compras)}</p>
                  <p>Total de Compras</p>
                </div>
              </div>
            </div>

            <div className="kpi-card info">
              <div className="card-content">
                <div className="thumb">
                  <FiPercent />
                </div>
                <div className="text">
                  <p>{formatNumber(totals.impostos)}</p>
                  <p>Total de Impostos</p>
                </div>
              </div>
            </div>
          </div>

          {/* KPI Cards de Impostos */}
          <div className="kpi-cards tax-stats">
            <div className="kpi-card compact tax-icms">
              <div className="card-content">
                <div className="thumb" />
                <div className="text">
                  <p>{formatNumber(taxTotals.icms)}</p>
                  <p>ICMS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact tax-piscofins">
              <div className="card-content">
                <div className="thumb" />
                <div className="text">
                  <p>{formatNumber(taxTotals.pisCofins)}</p>
                  <p>PIS/COFINS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact tax-ipi">
              <div className="card-content">
                <div className="thumb" />
                <div className="text">
                  <p>{formatNumber(taxTotals.ipi || 0)}</p>
                  <p>IPI</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact tax-ibs">
              <div className="card-content">
                <div className="thumb" />
                <div className="text">
                  <p>{formatNumber(taxTotals.ibs)}</p>
                  <p>IBS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact tax-cbs">
              <div className="card-content">
                <div className="thumb" />
                <div className="text">
                  <p>{formatNumber(taxTotals.cbs)}</p>
                  <p>CBS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact tax-is">
              <div className="card-content">
                <div className="thumb" />
                <div className="text">
                  <p>{formatNumber(taxTotals.is)}</p>
                  <p>IS</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className="filters-section">
            <div className="search-box">
              {isSearching ? (
                <FiLoader className="spin" style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <FiSearch />
              )}
              <input
                type="text"
                placeholder="Buscar por nome, CNPJ ou categoria do fornecedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.6,
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                  title="Limpar busca"
                >
                  <FiX style={{ fontSize: '16px' }} />
                </button>
              )}
            </div>
            {searchTerm && !isSearching && (
              <div style={{
                fontSize: '0.85em',
                opacity: 0.7,
                marginTop: '8px',
                marginLeft: '4px'
              }}>
                {filteredSuppliers.length === 0 ? (
                  <span style={{ color: '#ef4444' }}>Nenhum fornecedor encontrado</span>
                ) : (
                  <span>
                    {filteredSuppliers.length} fornecedor{filteredSuppliers.length !== 1 ? 'es' : ''} encontrado{filteredSuppliers.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Conteúdo Principal */}
          <MainContent>
            {/* Tabela de Fornecedores */}
            <div className="suppliers-table">
              <div className="table-header">
                <div className="header-cell fornecedor">Fornecedor</div>
                <div className="header-cell">ICMS</div>
                <div className="header-cell">PIS</div>
                <div className="header-cell">COFINS</div>
                <div className="header-cell">Compras</div>
                <div className="header-cell">Impostos</div>
              </div>

              <div className="table-body">
                {displayedSuppliers.map((supplier, index) => {
                  const totalImpostos = calculateTotalTaxes(supplier);
                  let cat = supplier.categoria || supplier.segmento;
                  if (!cat || cat === "Geral") {
                    cat = tipoPessoaLabels[supplier.tipoPessoa] || supplier.tipoPessoa || "Outros";
                  }
                  return (
                    <EntityListRow
                      key={`supplier-${supplier.id || index}`}
                      entity={supplier}
                      color={getSupplierColor(supplier.cnpj)}
                      icon={GoPackage}
                      eyebrow={supplier.cnpj}
                      title={supplier.nome}
                      tags={[
                        { label: cat, color: getTagColor(supplier.cnpj) },
                      ]}
                      columns={[
                        { width: "minmax(0, 2fr)" },
                        { width: "minmax(0, 1fr)", render: (s) => formatNumber(s.icms || 0) },
                        { width: "minmax(0, 1fr)", render: (s) => formatNumber((s.pisCofins || 0) * 0.35) },
                        { width: "minmax(0, 1fr)", render: (s) => formatNumber((s.pisCofins || 0) * 0.65) },
                        { width: "minmax(0, 1.2fr)", render: (s) => formatNumber(s.compras || s.faturamento || 0) },
                        { width: "minmax(0, 1fr)", render: (s) => formatNumber(calculateTotalTaxes(s)) },
                      ]}
                      onClick={() => setSelectedSupplier(supplier)}
                    />
                  );
                })}
                
                {/* Observer target for infinite scroll */}
                {hasMore && (
                  <div ref={observerTarget} style={{ padding: '20px', textAlign: 'center' }}>
                    {isLoadingMore && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
                        <span style={{ fontSize: '0.85em', opacity: 0.7 }}>Carregando mais fornecedores...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Card de Participação dos Fornecedores — Donut Compacto */}
            <div className="stats-section chart-card compact-categories">
              <div className="donut-wrapper">
                <div className="donut-header">
                  <h4>Participação dos Fornecedores</h4>
                  <p>Top mais expressivos no faturamento</p>
                </div>
                <div className="donut-chart">
                  <svg width="200" height="200" viewBox="0 0 200 200">
                    {(() => {
                      const topSuppliers = [...suppliersData]
                        .sort((a, b) => (b.compras || b.faturamento || 0) - (a.compras || a.faturamento || 0))
                        .slice(0, 7);
                      const total = topSuppliers.reduce((sum, s) => sum + (s.compras || s.faturamento || 0), 0);
                      if (total === 0) return null;
                      let currentAngle = -90;
                      const radius = 80;
                      const innerRadius = 50;
                      const cx = 100, cy = 100;
                      return topSuppliers.map((supplier, index) => {
                        const valor = supplier.compras || supplier.faturamento || 0;
                        const percentage = valor / total;
                        const angle = percentage * 360;
                        const startAngle = currentAngle;
                        const endAngle = currentAngle + angle;
                        const startRad = (startAngle * Math.PI) / 180;
                        const endRad = (endAngle * Math.PI) / 180;
                        const largeArc = angle > 180 ? 1 : 0;
                        const x1 = cx + radius * Math.cos(startRad);
                        const y1 = cy + radius * Math.sin(startRad);
                        const x2 = cx + radius * Math.cos(endRad);
                        const y2 = cy + radius * Math.sin(endRad);
                        const ix1 = cx + innerRadius * Math.cos(startRad);
                        const iy1 = cy + innerRadius * Math.sin(startRad);
                        const ix2 = cx + innerRadius * Math.cos(endRad);
                        const iy2 = cy + innerRadius * Math.sin(endRad);
                        const path = `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;
                        const midAngle = startAngle + angle / 2;
                        const midRad = (midAngle * Math.PI) / 180;
                        const labelR = (radius + innerRadius) / 2;
                        const labelX = cx + labelR * Math.cos(midRad);
                        const labelY = cy + labelR * Math.sin(midRad);
                        currentAngle += angle;
                        const color = getSupplierColor(supplier.cnpj);
                        return (
                          <g key={index}>
                            <path d={path} fill={color} stroke="none" />
                            {percentage > 0.08 && (
                              <text
                                x={labelX}
                                y={labelY}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fill="#ffffff"
                                fontSize="11"
                                fontWeight="700"
                              >
                                {(percentage * 100).toFixed(0)}%
                              </text>
                            )}
                          </g>
                        );
                      });
                    })()}
                  </svg>
                  <div className="donut-center">
                    <span className="donut-total">
                      {suppliersData.length}
                    </span>
                    <span className="donut-label">Fornecedores</span>
                  </div>
                </div>
                <div className="categories-list">
                  {(() => {
                    const topSuppliers = [...suppliersData]
                      .sort((a, b) => (b.compras || b.faturamento || 0) - (a.compras || a.faturamento || 0))
                      .slice(0, 7);
                    const maxCount = Math.max(...topSuppliers.map((s) => s.compras || s.faturamento || 0), 1);
                    const totalCompras = totals.compras || 1;
                    return topSuppliers.map((supplier, index) => {
                      const valor = supplier.compras || supplier.faturamento || 0;
                      const color = getSupplierColor(supplier.cnpj);
                      const nome = supplier.nome?.length > 22 ? supplier.nome.substring(0, 22) + "..." : (supplier.nome || "N/A");
                      return (
                        <div key={index} className="category-item">
                          <div className="category-header">
                            <div className="category-color" style={{ backgroundColor: color }}></div>
                            <span className="category-name">{nome}</span>
                            <span className="category-count">{((valor / totalCompras) * 100).toFixed(1)}%</span>
                          </div>
                          <div className="category-bar">
                            <div
                              className="category-progress"
                              style={{ width: `${(valor / maxCount) * 100}%`, backgroundColor: color }}
                            ></div>
                          </div>
                          <div className="category-value">{formatNumber(valor)}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </MainContent>

        </>
      )}

      {activeTab === "estatisticas" && (
        <div className="stats-tab">
          {/* KPIs Estratégicos */}
          <div className="stats-kpi-grid">
            <div className="stats-kpi-card primary">
              <div className="kpi-header">
                <FiTruck className="kpi-icon" />
                <span className="kpi-badge">SPED EFD</span>
              </div>
              <div className="kpi-value">{formatNumber(totals.compras)}</div>
              <div className="kpi-label">Volume Total de Compras</div>
              <div className="kpi-trend positive">
                <FiTrendingUp /> +12,5% vs período anterior
              </div>
            </div>

            <div className="stats-kpi-card success">
              <div className="kpi-header">
                <FiUsers className="kpi-icon" />
                <span className="kpi-badge">ATIVOS</span>
              </div>
              <div className="kpi-value">{dashboardData.kpis?.fornecedores || suppliersData.length}</div>
              <div className="kpi-label">Fornecedores Ativos</div>
              <div className="kpi-trend neutral">
                <FiBarChart2 /> {totalCategories} categorias
              </div>
            </div>

            <div className="stats-kpi-card warning">
              <div className="kpi-header">
                <FiPackage className="kpi-icon" />
                <span className="kpi-badge">TICKET</span>
              </div>
              <div className="kpi-value">
                {formatNumber(dashboardData.kpis?.ticketMedio || (totals.compras / (dashboardData.kpis?.fornecedores || suppliersData.length)))}
              </div>
              <div className="kpi-label">Ticket Médio por Fornecedor</div>
              <div className="kpi-trend positive">
                <FiTrendingUp /> +8,3% vs período anterior
              </div>
            </div>

            <div className="stats-kpi-card info">
              <div className="kpi-header">
                <GoLocation className="kpi-icon" />
                <span className="kpi-badge">UFs</span>
              </div>
              <div className="kpi-value">
                {dashboardData.geo?.length || new Set(suppliersData.map((s) => s.uf)).size}
              </div>
              <div className="kpi-label">Estados com Fornecedores</div>
              <div className="kpi-trend neutral">
                <FiMapPin /> Cobertura nacional
              </div>
            </div>
          </div>

          {/* Linha 1 - Distribuição Geográfica */}
          <div className="stats-row" style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: "20px" }}>
            {/* Top Estados */}
            <div className="stats-card top-estados">
              <div className="card-header">
                <div className="header-title">
                  <GoLocation />
                  <h4>Top Estados</h4>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  padding: "12px",
                  overflowY: "auto",
                  paddingRight: "4px",
                }}
                className="custom-scrollbar"
              >
                {(() => {
                  const regioes = {
                    SP: "Sudeste",
                    RJ: "Sudeste",
                    MG: "Sudeste",
                    ES: "Sudeste",
                    RS: "Sul",
                    PR: "Sul",
                    SC: "Sul",
                    BA: "Nordeste",
                    PE: "Nordeste",
                    CE: "Nordeste",
                    GO: "Centro-Oeste",
                    MT: "Centro-Oeste",
                    MS: "Centro-Oeste",
                    DF: "Centro-Oeste",
                    AM: "Norte",
                    PA: "Norte",
                    AC: "Norte",
                    RO: "Norte",
                  };
                  const nomeEstados = {
                    SP: "São Paulo",
                    RJ: "Rio de Janeiro",
                    MG: "Minas Gerais",
                    RS: "Rio Grande do Sul",
                    PR: "Paraná",
                    SC: "Santa Catarina",
                    BA: "Bahia",
                    PE: "Pernambuco",
                    GO: "Goiás",
                  };
                  // Usar dados da API se disponível, senão calcular do suppliersData
                  let estadosData = [];
                  if (dashboardData.geo && dashboardData.geo.length > 0) {
                    estadosData = dashboardData.geo.map(g => ({
                      uf: g.uf || g.id,
                      compras: g.faturamento || 0,
                      count: g.count || 0
                    }));
                  } else {
                    const ufMap = new Map();
                    suppliersData.forEach((supplier) => {
                      const uf = supplier.uf;
                      if (!uf) return;
                      if (!ufMap.has(uf))
                        ufMap.set(uf, { uf, compras: 0, count: 0 });
                      const data = ufMap.get(uf);
                      data.compras += supplier.compras || 0;
                      data.count += 1;
                    });
                    estadosData = Array.from(ufMap.values());
                  }
                  
                  const totalCompras = estadosData.reduce(
                    (sum, d) => sum + d.compras,
                    0
                  );
                  const sorted = estadosData.sort(
                    (a, b) => b.compras - a.compras
                  );
                  const maxCompras = sorted[0]?.compras || 1;
                  const rankColors = [
                    "#10b981",
                    "#3b82f6",
                    "#8b5cf6",
                    "#f59e0b",
                    "#64748b",
                  ];

                  return sorted.map((estado, index) => {
                    const percent = (estado.compras / totalCompras) * 100;
                    const barWidth = (estado.compras / maxCompras) * 100;
                    const color = rankColors[index] || "#64748b";

                    return (
                      <div
                        key={estado.uf}
                        style={{
                          background: `linear-gradient(135deg, ${color}08 0%, transparent 100%)`,
                          borderRadius: "10px",
                          padding: "12px 14px",
                          border: `1px solid ${color}20`,
                          transition: "all 0.2s ease",
                          cursor: "pointer",
                          position: "relative",
                          overflow: "hidden",
                          minHeight: "52px",
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateX(4px)";
                          e.currentTarget.style.borderColor = `${color}40`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateX(0)";
                          e.currentTarget.style.borderColor = `${color}20`;
                        }}
                      >
                        {/* Barra de progresso de fundo */}
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            height: "100%",
                            width: `${barWidth}%`,
                            background: `linear-gradient(90deg, ${color}15 0%, ${color}05 100%)`,
                            borderRadius: "10px",
                            transition: "width 0.5s ease",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            position: "relative",
                            zIndex: 1,
                          }}
                        >
                          {/* Lado esquerdo */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            {/* Badge de posição */}
                            <div
                              style={{
                                width: "28px",
                                height: "28px",
                                borderRadius: "8px",
                                background:
                                  index < 3
                                    ? `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`
                                    : "rgba(100, 116, 139, 0.2)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.75em",
                                fontWeight: 700,
                                color: index < 3 ? "#fff" : "#94a3b8",
                                boxShadow:
                                  index < 3 ? `0 2px 8px ${color}40` : "none",
                              }}
                            >
                              {index + 1}
                            </div>
                            {/* Info do estado */}
                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.9em",
                                    fontWeight: 700,
                                    color: theme.text,
                                  }}
                                >
                                  {estado.uf}
                                </span>
                                <span
                                  style={{
                                    fontSize: "0.75em",
                                    color: "#64748b",
                                  }}
                                >
                                  •
                                </span>
                                <span
                                  style={{
                                    fontSize: "0.8em",
                                    fontWeight: 500,
                                    color: "#94a3b8",
                                  }}
                                >
                                  {nomeEstados[estado.uf] || estado.uf}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginTop: "2px",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.65em",
                                    color: "#64748b",
                                    background: "rgba(100, 116, 139, 0.1)",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  {regioes[estado.uf] || "Brasil"}
                                </span>
                                <span
                                  style={{
                                    fontSize: "0.65em",
                                    color: "#64748b",
                                  }}
                                >
                                  {estado.count} fornecedor
                                  {estado.count > 1 ? "es" : ""}
                                </span>
                              </div>
                            </div>
                          </div>
                          {/* Lado direito - Valores */}
                          <div style={{ textAlign: "right" }}>
                            <div
                              style={{
                                fontSize: "0.95em",
                                fontWeight: 700,
                                color: color,
                              }}
                            >
                              {formatNumber(estado.compras)}
                            </div>
                            <div
                              style={{
                                fontSize: "0.65em",
                                color: "#64748b",
                                background: `${color}15`,
                                padding: "2px 8px",
                                borderRadius: "10px",
                                marginTop: "2px",
                                display: "inline-block",
                              }}
                            >
                              {percent.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Mapa - Compras por Estado (D3.js) */}
            <BrazilMapCard
              data={dashboardData.geo?.length > 0 ? dashboardData.geo : suppliersData}
              formatNumber={formatNumber}
            />
          </div>

          {/* Linha 2 - Top Fornecedores e NCM */}
          <div className="stats-row">
            {/* Top Fornecedores Mais Expressivos */}
            <div className="stats-card top-suppliers">
              <div className="card-header">
                <div className="header-title">
                  <FiTruck />
                  <h4>Fornecedores Mais Expressivos</h4>
                </div>
                <span className="header-badge">Top 5</span>
              </div>
              <div className="top-list">
                {[...suppliersData]
                  .sort((a, b) => (b.compras || b.faturamento || 0) - (a.compras || a.faturamento || 0))
                  .slice(0, 5)
                  .map((supplier, index) => {
                    const valor = supplier.compras || supplier.faturamento || 0;
                    const percentage = totals.compras > 0
                      ? (valor / totals.compras) * 100
                      : 0;
                    // Usar tipoPessoa se categoria/segmento for "Geral" ou vazio
                    let cat = supplier.categoria || supplier.segmento;
                    if (!cat || cat === "Geral") {
                      cat = tipoPessoaLabels[supplier.tipoPessoa] || supplier.tipoPessoa || "Outros";
                    }
                    return (
                      <div key={supplier.id || index} className="top-item">
                        <div className="rank-badge">#{index + 1}</div>
                        <div className="supplier-info">
                          <div className="supplier-name">{supplier.nome}</div>
                          <div className="supplier-meta">
                            <span className="cnpj">{supplier.cnpj}</span>
                            <span className="category">
                              {cat}
                            </span>
                          </div>
                        </div>
                        <div className="supplier-stats">
                          <div className="stat-value">
                            {formatNumber(valor)}
                          </div>
                          <div className="stat-bar">
                            <div
                              className="bar-fill"
                              style={{
                                width: `${percentage}%`,
                                background:
                                  index === 0
                                    ? "#10b981"
                                    : index === 1
                                    ? "#3b82f6"
                                    : "#8b5cf6",
                              }}
                            />
                          </div>
                          <div className="stat-percent">
                            {percentage.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Análise por NCM */}
            <div className="stats-card ncm-analysis">
              <div className="card-header">
                <div className="header-title">
                  <FiPackage />
                  <h4>Principais NCMs Adquiridos</h4>
                </div>
                <span className="header-badge">Classificação Fiscal</span>
              </div>
              <div className="ncm-list-pro">
                {(() => {
                  const ncmData = [
                    {
                      ncm: "8471.30.19",
                      desc: "Máquinas automáticas p/ processamento de dados",
                      valor: 4200000,
                      aliq: "0%",
                    },
                    {
                      ncm: "7308.90.90",
                      desc: "Construções e partes de ferro/aço",
                      valor: 3100000,
                      aliq: "12%",
                    },
                    {
                      ncm: "3926.90.90",
                      desc: "Outras obras de plástico",
                      valor: 2800000,
                      aliq: "18%",
                    },
                    {
                      ncm: "8544.49.00",
                      desc: "Outros condutores elétricos",
                      valor: 2100000,
                      aliq: "15%",
                    },
                    {
                      ncm: "4016.99.90",
                      desc: "Outras obras de borracha vulcanizada",
                      valor: 1500000,
                      aliq: "18%",
                    },
                  ];
                  const totalValor = ncmData.reduce(
                    (sum, item) => sum + item.valor,
                    0
                  );
                  const maxValor = ncmData[0].valor;

                  return ncmData.map((item, index) => {
                    const percent = (item.valor / totalValor) * 100;
                    const barWidth = (item.valor / maxValor) * 100;

                    return (
                      <div key={item.ncm} className="ncm-item-pro">
                        <div className="ncm-rank-pro">{index + 1}</div>
                        <div className="ncm-main">
                          <div className="ncm-info-pro">
                            <span className="ncm-code-pro">{item.ncm}</span>
                            <span className="ncm-desc-pro">{item.desc}</span>
                          </div>
                          <div className="ncm-bar-pro">
                            <div
                              className="bar-fill-pro"
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                        </div>
                        <div className="ncm-values-pro">
                          <div className="ncm-amount">
                            {formatNumber(item.valor)}
                          </div>
                          <div className="ncm-details">
                            <span className="ncm-pct">
                              {percent.toFixed(1)}%
                            </span>
                            <span className="ncm-ipi">IPI {item.aliq}</span>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>

          {/* Linha 3 - Distribuição de Impostos e Evolução */}
          <div className="stats-row">
            {/* Distribuição de Impostos */}
            <div className="stats-card category-analysis">
              <div className="card-header">
                <div className="header-title">
                  <FiDollarSign />
                  <h4>Distribuição de Impostos</h4>
                </div>
                <span className="header-badge">SPED Fiscal</span>
              </div>
              <div className="category-list">
                {(() => {
                  const impostos = [
                    {
                      nome: "ICMS",
                      valor: taxTotals.icms,
                      color: "#f59e0b",
                      desc: "Imposto sobre Circulação de Mercadorias"
                    },
                    {
                      nome: "PIS/COFINS",
                      valor: taxTotals.pisCofins,
                      color: "#10b981",
                      desc: "Contribuições Sociais"
                    },
                    {
                      nome: "IPI",
                      valor: taxTotals.ipi || 0,
                      color: "#8b5cf6",
                      desc: "Imposto sobre Produtos Industrializados"
                    }
                  ];

                  const totalImpostos = impostos.reduce((sum, imp) => sum + imp.valor, 0);
                  const maxValor = Math.max(...impostos.map(imp => imp.valor), 1);

                  return impostos.map((imposto) => {
                    const percent = totalImpostos > 0 ? (imposto.valor / totalImpostos) * 100 : 0;
                    const barWidth = (imposto.valor / maxValor) * 100;

                    return (
                      <div
                        key={imposto.nome}
                        className="category-item"
                        style={{ borderLeftColor: imposto.color }}
                      >
                        <div className="category-info">
                          <div className="category-name">{imposto.nome}</div>
                          <div className="category-meta">
                            {imposto.desc}
                          </div>
                        </div>
                        <div className="category-stats">
                          <div className="category-value" style={{ color: imposto.color }}>
                            {formatNumber(imposto.valor)}
                          </div>
                          <div className="category-bar">
                            <div
                              className="bar-fill"
                              style={{
                                width: `${barWidth}%`,
                                background: imposto.color,
                              }}
                            />
                          </div>
                          <div className="category-percent">
                            {percent.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Gráfico de Evolução Mensal */}
            <div className="stats-card evolution-chart">
              <div className="card-header">
                <div className="header-title">
                  <FiTrendingUp />
                  <h4>Evolução Mensal de Compras</h4>
                </div>
                <div className="chart-legend">
                  <div className="legend-item">
                    <div
                      className="legend-color"
                      style={{ backgroundColor: "#3b82f6" }}
                    ></div>
                    <span>Compras</span>
                  </div>
                </div>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={dashboardData.evolucao?.length > 0 ? dashboardData.evolucao.map(e => ({ mes: e.mes, compras: e.faturamento || 0 })) : []}
                    margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
                    barGap={4}
                  >
                    <defs>
                      <linearGradient
                        id="gradCompras"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#2563eb"
                          stopOpacity={1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={theme.border}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="mes"
                      tick={{ fill: theme.text, fontSize: 10, opacity: 0.7 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: theme.text, fontSize: 9, opacity: 0.6 }}
                      axisLine={false}
                      tickLine={false}
                      width={60}
                      tickFormatter={(value) => formatNumber(value)}
                    />
                    <Tooltip
                      cursor={false}
                      contentStyle={{
                        background: theme.card,
                        border: `1px solid ${theme.border}`,
                        borderRadius: "8px",
                      }}
                      formatter={(value) => [formatNumber(value), "Compras"]}
                    />
                    <Bar
                      dataKey="compras"
                      name="Compras"
                      fill="url(#gradCompras)"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== TAB REFORMA TRIBUTÁRIA ========== */}
      {activeTab === "reforma" && (
        <div className="reforma-tab">
          {/* Botões de Cenário */}
          <div className="cenario-buttons">
            <button 
              className="cenario-btn active"
            >
              <span className="cenario-year">2027</span>
              <span className="cenario-label">Início IVA Dual</span>
            </button>
            <button 
              className="cenario-btn"
            >
              <span className="cenario-year">2028</span>
              <span className="cenario-label">Transição 0%</span>
            </button>
            <button 
              className="cenario-btn"
            >
              <span className="cenario-year">2029</span>
              <span className="cenario-label">Transição 10%</span>
            </button>
            <button 
              className="cenario-btn"
            >
              <span className="cenario-year">2030</span>
              <span className="cenario-label">Transição 20%</span>
            </button>
            <button 
              className="cenario-btn"
            >
              <span className="cenario-year">2031</span>
              <span className="cenario-label">Transição 30%</span>
            </button>
            <button 
              className="cenario-btn"
            >
              <span className="cenario-year">2032</span>
              <span className="cenario-label">Transição 40%</span>
            </button>
            <button 
              className="cenario-btn"
            >
              <span className="cenario-year">2033</span>
              <span className="cenario-label">IVA Dual Pleno</span>
            </button>
          </div>

          {/* Linha 1 - Visão Geral */}
          <div className="kpi-cards main-stats">
            <div className="kpi-card primary">
              <div className="card-content">
                <div className="thumb">
                  <FiPackage />
                </div>
                <div className="text">
                  <p>{formatNumber(totals.compras)}</p>
                  <p>Valor Total de Compras</p>
                </div>
              </div>
            </div>

            <div className="kpi-card warning">
              <div className="card-content">
                <div className="thumb">
                  <FiUsers />
                </div>
                <div className="text">
                  <p>{dashboardData.kpis?.clientes || suppliersData.length}</p>
                  <p>Total de Fornecedores</p>
                </div>
              </div>
            </div>

            <div className="kpi-card info">
              <div className="card-content">
                <div className="thumb">
                  <FiBarChart2 />
                </div>
                <div className="text">
                  <p>{categoriesData.length}</p>
                  <p>Categorias Ativas</p>
                </div>
              </div>
            </div>

            <div className="kpi-card cte">
              <div className="card-content">
                <div className="thumb">
                  <FiPercent />
                </div>
                <div className="text">
                  <p>17.75%</p>
                  <p>Alíquota Efetiva (2027)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Linha 2 - Créditos Tributários Atuais */}
          <div className="kpi-cards secondary-stats">
            <div className="kpi-card compact warning with-progress">
              <div className="card-content">
                <div className="thumb">
                  <FiDollarSign />
                </div>
                <div className="text">
                  <p>{formatNumber(totals.impostos)}</p>
                  <p>Créditos Tributários Atuais</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatNumber(taxTotals.icms)}</p>
                  <p>ICMS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact nfe">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatNumber(taxTotals.pisCofins * 0.76)}</p>
                  <p>COFINS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact info">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatNumber(taxTotals.pisCofins * 0.24)}</p>
                  <p>PIS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact cte">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatNumber(taxTotals.is || 0)}</p>
                  <p>IPI</p>
                </div>
              </div>
            </div>
          </div>

          {/* Linha 3 - IVA Dual Projetado */}
          <div className="kpi-cards secondary-stats">
            <div className="kpi-card compact success with-progress">
              <div className="card-content">
                <div className="thumb">
                  <FiDollarSign />
                </div>
                <div className="text">
                  <p>{formatNumber(totals.compras * 0.1775)}</p>
                  <p>IVA Dual Projetado (2027)</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact success">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatNumber(totals.compras * 0.088)}</p>
                  <p>CBS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact info">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>8.80%</p>
                  <p>Alíquota CBS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact primary">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatNumber(totals.compras * 0.0895)}</p>
                  <p>IBS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact cte">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>8.95%</p>
                  <p>Alíquota IBS</p>
                </div>
              </div>
            </div>
          </div>

          {/* Busca de Fornecedores */}
          <div className="reforma-search">
            <div className="search-input-wrapper">
              <GoSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar fornecedores por nome, CNPJ ou categoria..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Tabela de Fornecedores com Impostos + Card Impacto */}
          <div className="reforma-content-row">
            <div className="reforma-products-table">
              <div className="table-header">
                <div className="header-cell produto">Fornecedor</div>
                <div className="header-cell">ICMS</div>
                <div className="header-cell">COFINS</div>
                <div className="header-cell">PIS</div>
                <div className="header-cell">IPI</div>
                <div className="header-cell">CBS</div>
                <div className="header-cell">IBS</div>
              </div>
              <div className="table-body">
                {reformaSuppliers.map((supplier, index) => {
                  const valor = supplier.compras || supplier.faturamento || 0;
                  return (
                    <div
                      key={supplier.id || index}
                      className="product-row"
                      style={{
                        "--product-color": [
                          "#3b82f6",
                          "#10b981",
                          "#f59e0b",
                          "#8b5cf6",
                          "#ef4444",
                        ][index % 5],
                        cursor: "pointer",
                      }}
                      onClick={() => setSelectedSupplier(supplier)}
                    >
                      <div className="product-info">
                        <span className="product-code">{supplier.cnpj}</span>
                        <span className="product-name">{supplier.nome}</span>
                      </div>
                      <div className="tax-value icms">
                        {formatNumber(supplier.icms || 0)}
                      </div>
                      <div className="tax-value cofins">
                        {formatNumber((supplier.pisCofins || 0) * 0.76)}
                      </div>
                      <div className="tax-value pis">
                        {formatNumber((supplier.pisCofins || 0) * 0.24)}
                      </div>
                      <div className="tax-value ipi">
                        {formatNumber(supplier.is || 0)}
                      </div>
                      <div className="tax-value cbs">
                        {formatNumber(valor * 0.088)}
                      </div>
                      <div className="tax-value ibs">
                        {formatNumber(valor * 0.0895)}
                      </div>
                    </div>
                  );
                })}
                
                {/* Observer target for infinite scroll */}
                {reformaHasMore && (
                  <div ref={reformaObserverTarget} style={{ padding: '20px', textAlign: 'center' }}>
                    {reformaIsLoadingMore && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
                        <span>Carregando mais fornecedores...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Card Impacto da Reforma */}
            <div className="impacto-sidebar">
              {/* Card Principal - Impacto */}
              <div className="impacto-main-card">
                <div className="impacto-header">
                  <div className="impacto-icon">
                    <GoGraph />
                  </div>
                  <span className="impacto-badge alto">
                    ALTO IMPACTO
                  </span>
                </div>
                <span className="impacto-title">Impacto da Reforma</span>
                <span className="impacto-value">{formatNumber(Math.abs(totals.compras * 0.1775 - totals.impostos))}</span>
                <span className="impacto-desc">
                  {(totals.compras * 0.1775 - totals.impostos) >= 0 ? 'Aumento tributário projetado' : 'Economia tributária projetada'}
                </span>
                <div className="impacto-variation">
                  {(totals.compras * 0.1775 - totals.impostos) >= 0 ? <GoArrowUp /> : <GoArrowDown />}
                  <span>{(totals.compras * 0.1775 - totals.impostos) >= 0 ? '+' : ''}{totals.impostos > 0 ? (((totals.compras * 0.1775 - totals.impostos) / totals.impostos) * 100).toFixed(1) : 0}%</span>
                  <span className="variation-label">vs. sistema atual</span>
                </div>
              </div>

              {/* Card Comparativo */}
              <div className="impacto-compare-card">
                <span className="compare-title">Comparativo de Carga</span>
                <div className="compare-items">
                  <div className="compare-item">
                    <span className="compare-label">Sistema Atual</span>
                    <span className="compare-value atual">{formatNumber(totals.impostos)}</span>
                  </div>
                  <div className="compare-item">
                    <span className="compare-label">IVA Dual (2027)</span>
                    <span className="compare-value novo">{formatNumber(totals.compras * 0.1775)}</span>
                  </div>
                  <div className="compare-item highlight">
                    <span className="compare-label">Diferença</span>
                    <span className="compare-value diff">{(totals.compras * 0.1775 - totals.impostos) >= 0 ? '+' : ''}{formatNumber(totals.compras * 0.1775 - totals.impostos)}</span>
                  </div>
                </div>
              </div>

              {/* Card Alíquotas */}
              <div className="impacto-aliquotas-card">
                <span className="aliquotas-title">Alíquotas Projetadas</span>
                <div className="aliquota-row">
                  <div className="aliquota-item">
                    <span className="aliquota-sigla">CBS</span>
                    <span className="aliquota-valor">8.80%</span>
                  </div>
                  <div className="aliquota-item">
                    <span className="aliquota-sigla">IBS</span>
                    <span className="aliquota-valor">8.95%</span>
                  </div>
                </div>
                <div className="aliquota-total">
                  <span>Alíquota Efetiva Total</span>
                  <span className="total-valor">17.75%</span>
                </div>
              </div>

              {/* Card Timeline */}
              <div className="impacto-timeline-card">
                <span className="timeline-title">Cronograma de Transição</span>
                <div className="timeline-steps">
                  <div className="timeline-step active">
                    <span className="step-year">2027</span>
                    <span className="step-desc">Início</span>
                  </div>
                  <div className="timeline-step">
                    <span className="step-year">2029</span>
                    <span className="step-desc">40%</span>
                  </div>
                  <div className="timeline-step">
                    <span className="step-year">2033</span>
                    <span className="step-desc">100%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Gráficos de Barras */}
          <div className="reforma-charts-row">
            {/* Gráfico 1 - Comparativo de Tributos */}
            <div className="reforma-chart-card">
              <div className="chart-header">
                <div className="chart-title">
                  <HiOutlineChartBar className="chart-icon" />
                  <span>Comparativo de Tributos</span>
                </div>
                <span className="chart-subtitle">
                  Sistema Atual vs IVA Dual
                </span>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart
                    data={[
                      { name: "ICMS", atual: taxTotals.icms, novo: 0 },
                      { name: "PIS", atual: taxTotals.pisCofins * 0.24, novo: 0 },
                      { name: "COFINS", atual: taxTotals.pisCofins * 0.76, novo: 0 },
                      { name: "IPI", atual: taxTotals.is || 0, novo: 0 },
                      { name: "CBS", atual: 0, novo: totals.compras * 0.088 },
                      { name: "IBS", atual: 0, novo: totals.compras * 0.0895 },
                    ]}
                    margin={{ top: 5, right: 5, left: 35, bottom: 20 }}
                    barGap={2}
                    barCategoryGap="15%"
                  >
                    <defs>
                      <linearGradient
                        id="gradientAtual"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#9ca3af" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#6b7280"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="gradientNovo"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#3b82f6"
                          stopOpacity={1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={theme.border}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{
                        fill: theme.text,
                        fontSize: 11,
                        fontWeight: 500,
                        opacity: 0.7,
                      }}
                      axisLine={false}
                      tickLine={false}
                      dy={5}
                    />
                    <YAxis
                      tick={{ fill: theme.text, fontSize: 10, opacity: 0.6 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) =>
                        value >= 1000000
                          ? `${(value / 1000000).toFixed(1)}M`
                          : value >= 1000
                          ? `${(value / 1000).toFixed(0)}K`
                          : value
                      }
                      width={40}
                    />
                    <Tooltip
                      cursor={{ fill: theme.tableRowHover }}
                      contentStyle={{
                        background: theme.card,
                        border: `1px solid ${theme.border}`,
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        padding: "8px 12px",
                      }}
                      formatter={(value, name) => [
                        `R$ ${value.toLocaleString("pt-BR")}`,
                        name === "atual" ? "Sistema Atual" : "IVA Dual",
                      ]}
                      labelStyle={{
                        color: theme.text,
                        fontWeight: 600,
                        marginBottom: 4,
                      }}
                      itemStyle={{
                        color: theme.text,
                        opacity: 0.8,
                        fontSize: 12,
                      }}
                    />
                    <Bar
                      dataKey="atual"
                      name="atual"
                      fill="url(#gradientAtual)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={50}
                    />
                    <Bar
                      dataKey="novo"
                      name="novo"
                      fill="url(#gradientNovo)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={50}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-legend">
                <div className="legend-item">
                  <span
                    className="legend-dot"
                    style={{
                      background: "linear-gradient(180deg, #9ca3af, #6b7280)",
                    }}
                  ></span>
                  <span>Sistema Atual</span>
                </div>
                <div className="legend-item">
                  <span
                    className="legend-dot"
                    style={{
                      background: "linear-gradient(180deg, #60a5fa, #3b82f6)",
                    }}
                  ></span>
                  <span>IVA Dual (2027)</span>
                </div>
              </div>
            </div>

            {/* Gráfico 2 - Evolução da Carga Tributária */}
            <div className="reforma-chart-card">
              <div className="chart-header">
                <div className="chart-title">
                  <GoGraph className="chart-icon" />
                  <span>Evolução da Carga Tributária</span>
                </div>
                <span className="chart-subtitle">
                  Projeção por ano de transição
                </span>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={[
                      { ano: "2024", valor: totals.impostos, label: "Atual" },
                      { ano: "2027", valor: totals.compras * 0.1775, label: "Início" },
                      { ano: "2029", valor: totals.compras * 0.19, label: "40%" },
                      { ano: "2030", valor: totals.compras * 0.20, label: "60%" },
                      { ano: "2031", valor: totals.compras * 0.21, label: "80%" },
                      { ano: "2033", valor: totals.compras * 0.2275, label: "100%" },
                    ]}
                    margin={{ top: 30, right: 30, left: 60, bottom: 20 }}
                  >
                    <defs>
                      <linearGradient
                        id="barGradient2024"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#9ca3af" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#6b7280"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="barGradient2027"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#93c5fd" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#60a5fa"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="barGradient2029"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#3b82f6"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="barGradient2030"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#2563eb"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="barGradient2031"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#2563eb" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#1d4ed8"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="barGradient2033"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#1d4ed8" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#1e40af"
                          stopOpacity={1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={theme.border}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="ano"
                      tick={{
                        fill: theme.text,
                        fontSize: 12,
                        fontWeight: 500,
                        opacity: 0.7,
                      }}
                      axisLine={{
                        stroke: theme.border,
                        strokeWidth: 1,
                      }}
                      tickLine={false}
                      dy={10}
                    />
                    <YAxis
                      tick={{ fill: theme.text, fontSize: 11, opacity: 0.6 }}
                      axisLine={{
                        stroke: theme.border,
                        strokeWidth: 1,
                      }}
                      tickLine={false}
                      tickFormatter={(value) =>
                        value >= 1000000
                          ? `R$ ${(value / 1000000).toFixed(1)}M`
                          : value >= 1000
                          ? `R$ ${(value / 1000).toFixed(0)}K`
                          : `R$ ${value}`
                      }
                      width={55}
                    />
                    <Tooltip
                      cursor={{ fill: theme.tableRowHover }}
                      contentStyle={{
                        background: theme.card,
                        border: `1px solid ${theme.border}`,
                        borderRadius: "12px",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                        padding: "12px 16px",
                      }}
                      formatter={(value) => [
                        `R$ ${value.toLocaleString("pt-BR")}`,
                        "Carga Tributária",
                      ]}
                      labelStyle={{
                        color: theme.text,
                        fontWeight: 600,
                        marginBottom: 8,
                      }}
                      itemStyle={{
                        color: theme.text,
                        opacity: 0.8,
                        fontSize: 13,
                      }}
                    />
                    <Bar
                      dataKey="valor"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={55}
                    >
                      {[
                        { ano: "2024", fill: "url(#barGradient2024)" },
                        { ano: "2027", fill: "url(#barGradient2027)" },
                        { ano: "2029", fill: "url(#barGradient2029)" },
                        { ano: "2030", fill: "url(#barGradient2030)" },
                        { ano: "2031", fill: "url(#barGradient2031)" },
                        { ano: "2033", fill: "url(#barGradient2033)" },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-footer">
                <div className="footer-stat">
                  <span className="stat-label">Variação Total</span>
                  <span className={`stat-value ${(totals.compras * 0.1775 - totals.impostos) >= 0 ? 'increase' : 'decrease'}`}>
                    {(totals.compras * 0.1775 - totals.impostos) >= 0 ? '+' : ''}{totals.impostos > 0 ? (((totals.compras * 0.1775 - totals.impostos) / totals.impostos) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="footer-stat">
                  <span className="stat-label">{(totals.compras * 0.1775 - totals.impostos) >= 0 ? 'Aumento Absoluto' : 'Economia Absoluta'}</span>
                  <span className="stat-value">{(totals.compras * 0.1775 - totals.impostos) >= 0 ? '+' : ''}{formatNumber(totals.compras * 0.1775 - totals.impostos)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default Suppliers;
